import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { css } from '@linaria/core'
import { Link } from '@fs/zion-router'
import { i18n } from '@fs/zion-locale'
import { colors, Separator } from '@fs/zion-ui'
import CookiePreferences from '@fs/zion-cookie-preferences'
import churchLinks from './ChurchLinks'
import { buildUrl } from '../footerHelperFunctions'
import AccessibilityLink from './AccessibilityLink'
import DynamicLinksOverride from './DynamicLinksOverride/Override'
import useDynamicLegalFooter from '../hooks/useDynamicLegalFooter'

const legalCss = css`
  color: ${colors.transparent.gray60};
  font-size: 12px;
  line-height: 1.75em;
`

const CHURCH_NAME_TOKEN = '{churchName}'

function getServiceProviderDefaults(serviceProviderText) {
  if (!serviceProviderText) return null

  return serviceProviderText.replace(CHURCH_NAME_TOKEN, '<0>{{churchName}}</0>')
}

function getStaticLegalLinks() {
  return [
    {
      id: 'terms',
      url: '/legal/terms',
      linkName: 'footer_terms',
      i18nKey: 'footer.terms-of-use',
      defaults: '<0>FamilySearch Terms of Use</0>',
    },
    {
      id: 'privacy',
      url: '/legal/privacy',
      linkName: 'footer_privacy',
      i18nKey: 'footer.privacy-notice',
      defaults: '<0>Privacy Notice</0>',
    },
    {
      id: 'country',
      url: '/locale',
      linkName: 'footer_country_and_lang_options',
      i18nKey: 'footer.country-and-language-options',
      defaults: '<0>Country and Language Options</0>',
    },
  ]
}

function buildLegalModel({ isEnabled, legalItems }) {
  const links = isEnabled
    ? ['terms', 'privacy', 'country'].map((id) => legalItems?.[id]).filter(Boolean)
    : getStaticLegalLinks()

  const accessibility = isEnabled ? legalItems?.accessibility || null : null

  return {
    links,
    accessibility,
  }
}

function getChurchLinkUrl(legalItems) {
  if (legalItems?.churchName?.url) {
    return legalItems.churchName.url
  }
  return churchLinks[i18n.language] || churchLinks.en
}

function LegalLinks({ links, external }) {
  return links.map((item, index) => (
    <React.Fragment key={item.id}>
      {index > 0 && <>&nbsp;|&nbsp;</>}
      {item.i18nKey ? (
        <Trans
          i18nKey={item.i18nKey}
          defaults={item.defaults}
          components={[
            <Link
              key={item.id}
              isGray
              external
              wayfinding
              to={buildUrl(item.url, external)}
              linkName={item.linkName}
            />,
          ]}
        />
      ) : (
        <Link isGray external wayfinding to={buildUrl(item.url, external)} linkName={item.linkName}>
          {item.text}
        </Link>
      )}
    </React.Fragment>
  ))
}

function DynamicCopyrightStatement({ legalItems }) {
  const [t] = useTranslation()
  const year = new Date().getFullYear()

  // legalItems is already translated
  if (legalItems?.copyright?.text) {
    return <Trans defaults={legalItems.copyright.text} values={{ year }} />
  }

  return t('footer.copyright-notice', '© {year} by Intellectual Reserve, Inc. All rights reserved.', { year })
}

function ServiceProviderText({ nonReligiousMode, hideChurchName, legalItems }) {
  const [t] = useTranslation()
  const churchLinkUrl = getChurchLinkUrl(legalItems)
  const churchName =
    legalItems?.churchName?.text || t('footer.church-name', 'The Church of Jesus Christ of Latter-day Saints')
  const serviceProviderText = legalItems?.serviceProvider?.text || null
  const linkName = legalItems?.churchName?.linkName || 'footer_service_provided_by_church'
  const serviceProviderDefaults = getServiceProviderDefaults(serviceProviderText)

  if (hideChurchName) return null

  if (!serviceProviderText) {
    if (nonReligiousMode) {
      return (
        <>
          {t('footer.service-by-church.text', 'A service provided by The Church of Jesus Christ of Latter-day Saints')}
        </>
      )
    }

    return (
      <Trans i18nKey="footer.service-by-church.link">
        A service provided by
        <Link isGray wayfinding to={churchLinkUrl} linkName={linkName} external>
          {churchName}
        </Link>
      </Trans>
    )
  }

  if (nonReligiousMode) {
    return <Trans defaults={serviceProviderText} values={{ churchName }} />
  }

  if (!serviceProviderText.includes(CHURCH_NAME_TOKEN)) return serviceProviderText

  return (
    <Trans
      defaults={serviceProviderDefaults}
      values={{ churchName }}
      components={[<Link key={linkName} isGray wayfinding to={churchLinkUrl} linkName={linkName} external />]}
    />
  )
}

export default function MinLegalText({
  menaReligiousMode,
  nonReligiousMode,
  showArabicExperience,
  external,
  ...props
}) {
  const [t] = useTranslation()
  const { isEnabled, legalItems } = useDynamicLegalFooter()

  const hideChurchName = menaReligiousMode || showArabicExperience
  const legalModel = buildLegalModel({ isEnabled, legalItems })

  return (
    <nav aria-label={t('nav.footer-legal', 'Legal links')} className={legalCss} {...props}>
      {legalModel.accessibility ? (
        <div>
          <Link
            isGray
            external
            wayfinding
            to={buildUrl(legalModel.accessibility.url, external)}
            linkName={legalModel.accessibility.linkName}
          >
            {legalModel.accessibility.text}
          </Link>
        </div>
      ) : (
        <AccessibilityLink external={external} />
      )}
      <Separator size="xxs" />
      <div>
        <DynamicLinksOverride />
        <LegalLinks links={legalModel.links} external={external} />
        &nbsp;|&nbsp;
        <CookiePreferences includeButton />
      </div>
      <Separator size="xxs" />
      <div>
        <DynamicCopyrightStatement legalItems={isEnabled ? legalItems : null} />{' '}
        <ServiceProviderText
          nonReligiousMode={nonReligiousMode}
          hideChurchName={hideChurchName}
          legalItems={isEnabled ? legalItems : null}
        />
      </div>
    </nav>
  )
}
